import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { DataProvider, useData } from './context/DataContext';
import { EvidenceDetailModal } from './components/Evidence/EvidenceDetailModal';

// Helper to handle failed dynamic imports
const lazyWithRetry = (importFn: () => Promise<any>) => {
  return lazy(() => 
    importFn().catch((error) => {
      console.error('Failed to load module:', error);
      // On import failure, reload the page once
      if (error.message.includes('Failed to fetch dynamically imported module')) {
        window.location.reload();
      }
      throw error;
    })
  );
};

// Lazy load pages to improve performance
const EvidenceExplorer = lazyWithRetry(() => import('./pages/EvidenceExplorerPage'));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'));
const ExportPage = lazyWithRetry(() => import('./pages/ExportPage'));
const EntityExplorerPage = lazyWithRetry(() => import('./pages/EntityExplorerPage'));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage'));
const EvidenceItemPage = lazyWithRetry(() => import('./pages/EvidenceItemPage'));
const TimelinePage = lazyWithRetry(() => import('./pages/TimelinePage'));

// Impact Streams components
const ImpactStreamsLayout = lazyWithRetry(() => import('./components/ImpactStreams/ImpactStreamsLayout'));
const ImpactStreamsOverview = lazyWithRetry(() => import('./components/ImpactStreams/ImpactStreamsOverview'));
const ModuleDetailView = lazyWithRetry(() => import('./components/ImpactStreams/ModuleDetailView'));
const StreamDetailView = lazyWithRetry(() => import('./components/ImpactStreams/StreamDetailView'));

// Loading component
const Loading = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// Error boundary component
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    // If it's a chunk loading error, try reloading once
    if (error.message && error.message.includes('Loading chunk')) {
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-red-50">
          <div className="text-center p-6 max-w-md bg-white rounded-lg shadow-md">
            <h2 className="text-2xl text-red-600 mb-4">Something went wrong</h2>
            <p className="text-gray-700 mb-4">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// AppContent component to wrap routes and modal
const AppContent = () => {
  const { isEvidenceModalOpen, closeEvidenceModal } = useData();
  
  return (
    <>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/evidence" element={<EvidenceExplorer />} />
          <Route path="/evidence/item" element={<EvidenceItemPage />} />
          <Route path="/entities" element={<EntityExplorerPage />} />
          <Route path="/timeline" element={<TimelinePage />} />
          <Route path="/export" element={<ExportPage />} />
          <Route path="/about" element={<AboutPage />} />
          
          {/* Impact Streams Routes */}
          <Route path="/impact-streams" element={<ImpactStreamsLayout />}>
            <Route index element={<ImpactStreamsOverview />} />
            <Route path="module/:moduleId" element={<ModuleDetailView />} />
            <Route path="stream/:streamId" element={<StreamDetailView />} />
          </Route>
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      
      {/* Evidence Detail Modal - available globally */}
      <EvidenceDetailModal isOpen={isEvidenceModalOpen} onClose={closeEvidenceModal} />
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <DataProvider>
          <AppContent />
        </DataProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
